import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaBuilding, FaEdit, FaTrash } from "react-icons/fa";
import Navbar from "../../components/Navbar";
import { companyProfileAPI, officeSpaceAPI } from "../../services/meetingsAPI";
import { toast } from "react-toastify";
import "./AddYourSpace.css";

const createEmptyCompanyProfile = () => ({
  displayName: "",
  industry: "",
  website: "",
  contactEmail: "",
  contactPhone: "",
  description: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  country: "",
  postalCode: "",
});

const AddYourSpace = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLoadingRooms, setIsLoadingRooms] = useState(true);
  
  // Rooms state
  const [rooms, setRooms] = useState([]);
  
  // Form states for adding/editing room
  const [editingRoomIndex, setEditingRoomIndex] = useState(null);
  const [formData, setFormData] = useState({
    roomName: "",
    roomNumber: "",
    companyName: "",
    description: "",
    images: [],
    amenities: [],
    visibility: "private",
  });
  const [companyProfileLoading, setCompanyProfileLoading] = useState(false);
  const [companyProfileSaving, setCompanyProfileSaving] = useState(false);
  const [hasCompanyProfile, setHasCompanyProfile] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(createEmptyCompanyProfile());

  const isCompanyAdmin = currentUser?.role === "company_admin" && Boolean(currentUser?.company?._id || currentUser?.company);

  const availableAmenities = [
    "WiFi",
    "Projector",
    "AC",
    "Whiteboard",
    "Parking",
    "Video Conferencing",
    "Coffee/Tea",
    "Catering",
    "Sound System",
    "Natural Light"
  ];

  const amenityIcons = {
    WiFi: "📶",
    Projector: "📽️",
    AC: "❄️",
    Whiteboard: "📝",
    Parking: "🅿️",
    "Video Conferencing": "📹",
    "Coffee/Tea": "☕",
    Catering: "🍽️",
    "Sound System": "🔊",
    "Natural Light": "☀️",
  };

  useEffect(() => {
    const checkAuth = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        if (storedUser && storedToken) {
          const parsedUser = JSON.parse(storedUser);
          setCurrentUser(parsedUser);
          setIsAuthenticated(true);
        } else {
          setCurrentUser(null);
          setIsAuthenticated(false);
          navigate('/meeting-home');
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        setCurrentUser(null);
        setIsAuthenticated(false);
        navigate('/meeting-home');
      }
    };

    checkAuth();
  }, [navigate]);

  // Fetch user's rooms
  useEffect(() => {
    const fetchUserRooms = async () => {
      if (!isAuthenticated || !currentUser) return;

      setIsLoadingRooms(true);
      try {
        const response = await officeSpaceAPI.getMyOfficeSpace();
        if (response.data.success && response.data.data) {
          const officeSpaceData = response.data.data;
          if (officeSpaceData.rooms && officeSpaceData.rooms.length > 0) {
            setRooms(officeSpaceData.rooms);
          } else {
            setRooms([]);
          }
        } else {
          setRooms([]);
        }
      } catch (error) {
        if (error.response?.status === 404) {
          setRooms([]);
        } else {
          console.error('Error fetching rooms:', error);
          toast.error('Failed to load your rooms');
        }
      } finally {
        setIsLoadingRooms(false);
      }
    };

    if (isAuthenticated && currentUser) {
      fetchUserRooms();
    }
  }, [isAuthenticated, currentUser]);

  useEffect(() => {
    const fetchCompanyProfile = async () => {
      if (!isAuthenticated || !currentUser || !isCompanyAdmin) {
        setHasCompanyProfile(false);
        setCompanyProfile(createEmptyCompanyProfile());
        return;
      }

      setCompanyProfileLoading(true);
      try {
        const response = await companyProfileAPI.getMyCompanyProfile();
        const payload = response?.data?.data || {};
        const incomingProfile = payload.profile || {};

        setHasCompanyProfile(Boolean(payload.hasProfile));
        setCompanyProfile({
          ...createEmptyCompanyProfile(),
          ...incomingProfile,
          displayName: incomingProfile.displayName || payload.name || currentUser?.company?.name || "",
        });
      } catch (error) {
        console.error("Error fetching company profile:", error);
        toast.error(error.response?.data?.message || "Failed to load company profile");
      } finally {
        setCompanyProfileLoading(false);
      }
    };

    fetchCompanyProfile();
  }, [isAuthenticated, currentUser, isCompanyAdmin]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleCompanyProfileChange = (field, value) => {
    setCompanyProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveCompanyProfile = async (e) => {
    e.preventDefault();

    if (!isCompanyAdmin) {
      return;
    }

    if (!hasCompanyProfile && !companyProfile.displayName.trim()) {
      toast.error("Company name is required to create profile");
      return;
    }

    const payload = {
      displayName: companyProfile.displayName.trim(),
      industry: companyProfile.industry.trim(),
      website: companyProfile.website.trim(),
      contactEmail: companyProfile.contactEmail.trim(),
      contactPhone: companyProfile.contactPhone.trim(),
      description: companyProfile.description.trim(),
      addressLine1: companyProfile.addressLine1.trim(),
      addressLine2: companyProfile.addressLine2.trim(),
      city: companyProfile.city.trim(),
      state: companyProfile.state.trim(),
      country: companyProfile.country.trim(),
      postalCode: companyProfile.postalCode.trim(),
    };

    setCompanyProfileSaving(true);
    try {
      const response = hasCompanyProfile
        ? await companyProfileAPI.updateCompanyProfile(payload)
        : await companyProfileAPI.createCompanyProfile(payload);

      const result = response?.data?.data || {};
      const nextProfile = result.profile || payload;

      setHasCompanyProfile(Boolean(result.hasProfile));
      setCompanyProfile({
        ...createEmptyCompanyProfile(),
        ...nextProfile,
        displayName: nextProfile.displayName || result.name || currentUser?.company?.name || "",
      });

      toast.success(response?.data?.message || "Company profile saved successfully");
    } catch (error) {
      console.error("Error saving company profile:", error);
      toast.error(error.response?.data?.message || "Failed to save company profile");
    } finally {
      setCompanyProfileSaving(false);
    }
  };


  const resetForm = () => {
    setFormData({
      roomName: "",
      roomNumber: "",
      companyName: "",
      description: "",
      images: [],
      amenities: [],
      visibility: "private",
    });
    setEditingRoomIndex(null);
  };

  const handleEdit = (index) => {
    const room = rooms[index];
    setFormData({
      roomName: room.roomName || "",
      roomNumber: room.roomNumber || "",
      companyName: room.companyName || "",
      description: room.description || "",
      images: room.images || [],
      amenities: room.amenities || [],
      visibility: room.visibility || "private",
    });
    setEditingRoomIndex(index);
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Are you sure you want to delete this room?')) {
      return;
    }

    const updatedRooms = rooms.filter((_, i) => i !== index);
    await saveRooms(updatedRooms);
  };

  const saveRooms = async (roomsToSave) => {
    if (!isAuthenticated) {
      toast.error('Please login to save rooms');
      return;
    }

    if (roomsToSave.length === 0 || roomsToSave.some(r => !r.roomName || r.roomName.trim() === '')) {
      toast.error('Please enter at least one room with a name');
      return;
    }

    setLoading(true);
    try {
      const officeSpaceData = {
        rooms: roomsToSave.map(room => ({
          roomName: room.roomName.trim(),
          roomNumber: room.roomNumber?.trim() || '',
          companyName: room.companyName?.trim() || '',
          description: room.description || '',
          images: room.images || [],
          amenities: room.amenities || [],
          visibility: room.visibility === "public" ? "public" : "private",
        }))
      };

      const response = await officeSpaceAPI.createOrUpdateOfficeSpace(officeSpaceData);
      
      if (response.data.success) {
        toast.success(response.data.message || 'Rooms saved successfully!');
        setRooms(roomsToSave);
        resetForm();
      } else {
        toast.error(response.data.message || 'Failed to save rooms');
      }
    } catch (error) {
      console.error('Error saving rooms:', error);
      toast.error(error.response?.data?.message || 'Failed to save rooms. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.roomName || formData.roomName.trim() === '') {
      toast.error('Please enter a room name');
      return;
    }

    let updatedRooms;
    if (editingRoomIndex !== null) {
      // Update existing room
      updatedRooms = rooms.map((room, index) =>
        index === editingRoomIndex
          ? {
              roomName: formData.roomName.trim(),
              roomNumber: formData.roomNumber.trim(),
              companyName: formData.companyName.trim(),
              description: formData.description.trim(),
              images: formData.images || [],
              amenities: formData.amenities,
              visibility: formData.visibility === "public" ? "public" : "private",
            }
          : room
      );
    } else {
      // Add new room
      updatedRooms = [
        ...rooms,
        {
          roomName: formData.roomName.trim(),
          roomNumber: formData.roomNumber.trim(),
          companyName: formData.companyName.trim(),
          description: formData.description.trim(),
          images: formData.images || [],
          amenities: formData.amenities,
          visibility: formData.visibility === "public" ? "public" : "private",
        }
      ];
    }

    await saveRooms(updatedRooms);
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="add-your-space-page page-content">
      <Navbar isScrolled={false} showLoginModal={true} />

      <div className="ays-container">
        <header className="ays-hero">
          <span className="ays-hero-badge">Workspace</span>
          <h1 className="ays-hero-title">Manage Your Space</h1>
          <p className="ays-hero-subtitle">
            {isCompanyAdmin
              ? "Set up your company profile and add meeting rooms your team can discover and book."
              : "Add private meeting rooms for your office. Only you and your company members can view and book them."}
          </p>
        </header>

        {isCompanyAdmin && (
          <section className="ays-card">
            <div className="ays-card-header">
              <div>
                <h2 className="ays-card-title">Company Profile</h2>
                <p className="ays-card-desc">
                  Create your company profile once, then update it whenever details change.
                </p>
              </div>
              <span className={`ays-status-pill ${hasCompanyProfile ? "ays-status-pill--ok" : "ays-status-pill--warn"}`}>
                {hasCompanyProfile ? "Profile Created" : "Not Created"}
              </span>
            </div>

            {companyProfileLoading ? (
              <div className="ays-loading">
                <div className="ays-loading-spinner" />
                <p>Loading company profile...</p>
              </div>
            ) : (
              <form onSubmit={handleSaveCompanyProfile}>
                <div className="ays-form-grid">
                  <div className="ays-field">
                    <label className="ays-label">
                      Company Display Name <span className="ays-label-required">*</span>
                    </label>
                    <input
                      type="text"
                      value={companyProfile.displayName}
                      onChange={(e) => handleCompanyProfileChange("displayName", e.target.value)}
                      className="ays-input"
                      placeholder="e.g., ABC Corporation"
                    />
                  </div>
                  <div className="ays-field">
                    <label className="ays-label">Industry</label>
                    <input
                      type="text"
                      value={companyProfile.industry}
                      onChange={(e) => handleCompanyProfileChange("industry", e.target.value)}
                      className="ays-input"
                      placeholder="e.g., IT Services"
                    />
                  </div>
                  <div className="ays-field">
                    <label className="ays-label">Website</label>
                    <input
                      type="text"
                      value={companyProfile.website}
                      onChange={(e) => handleCompanyProfileChange("website", e.target.value)}
                      className="ays-input"
                      placeholder="https://example.com"
                    />
                  </div>
                  <div className="ays-field">
                    <label className="ays-label">Contact Email</label>
                    <input
                      type="email"
                      value={companyProfile.contactEmail}
                      onChange={(e) => handleCompanyProfileChange("contactEmail", e.target.value)}
                      className="ays-input"
                      placeholder="contact@company.com"
                    />
                  </div>
                  <div className="ays-field">
                    <label className="ays-label">Contact Phone</label>
                    <input
                      type="text"
                      value={companyProfile.contactPhone}
                      onChange={(e) => handleCompanyProfileChange("contactPhone", e.target.value)}
                      className="ays-input"
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div className="ays-field">
                    <label className="ays-label">Postal Code</label>
                    <input
                      type="text"
                      value={companyProfile.postalCode}
                      onChange={(e) => handleCompanyProfileChange("postalCode", e.target.value)}
                      className="ays-input"
                      placeholder="560001"
                    />
                  </div>
                  <div className="ays-field ays-field--full">
                    <label className="ays-label">Company Description</label>
                    <textarea
                      value={companyProfile.description}
                      onChange={(e) => handleCompanyProfileChange("description", e.target.value)}
                      rows="3"
                      className="ays-textarea"
                      placeholder="Write a short profile summary for your company..."
                    />
                  </div>
                  <div className="ays-field">
                    <label className="ays-label">Address Line 1</label>
                    <input
                      type="text"
                      value={companyProfile.addressLine1}
                      onChange={(e) => handleCompanyProfileChange("addressLine1", e.target.value)}
                      className="ays-input"
                      placeholder="Building / Street"
                    />
                  </div>
                  <div className="ays-field">
                    <label className="ays-label">Address Line 2</label>
                    <input
                      type="text"
                      value={companyProfile.addressLine2}
                      onChange={(e) => handleCompanyProfileChange("addressLine2", e.target.value)}
                      className="ays-input"
                      placeholder="Area / Landmark"
                    />
                  </div>
                  <div className="ays-field">
                    <label className="ays-label">City</label>
                    <input
                      type="text"
                      value={companyProfile.city}
                      onChange={(e) => handleCompanyProfileChange("city", e.target.value)}
                      className="ays-input"
                      placeholder="City"
                    />
                  </div>
                  <div className="ays-field">
                    <label className="ays-label">State</label>
                    <input
                      type="text"
                      value={companyProfile.state}
                      onChange={(e) => handleCompanyProfileChange("state", e.target.value)}
                      className="ays-input"
                      placeholder="State"
                    />
                  </div>
                  <div className="ays-field">
                    <label className="ays-label">Country</label>
                    <input
                      type="text"
                      value={companyProfile.country}
                      onChange={(e) => handleCompanyProfileChange("country", e.target.value)}
                      className="ays-input"
                      placeholder="Country"
                    />
                  </div>
                </div>

                <div className="ays-actions">
                  <button
                    type="submit"
                    disabled={companyProfileSaving}
                    className="ays-btn ays-btn--primary ays-btn--inline"
                  >
                    {companyProfileSaving
                      ? "Saving..."
                      : hasCompanyProfile
                        ? "Update Company Profile"
                        : "Create Company Profile"}
                  </button>
                </div>
              </form>
              )}
          </section>
        )}

        <section className="ays-card">
          <div className="ays-card-header">
            <div>
              <h2 className="ays-card-title">
                {editingRoomIndex !== null ? "Edit Room" : "Add New Room"}
              </h2>
              <p className="ays-card-desc">
                Fill in room details, choose visibility, and select amenities for your team.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="ays-room-form">
            <div className="ays-form-grid">
              <div className="ays-field">
                <label className="ays-label">
                  Room Name / Number <span className="ays-label-required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.roomName}
                  onChange={(e) => handleInputChange("roomName", e.target.value)}
                  required
                  className="ays-input"
                  placeholder="e.g., Conference Room A, Room 101"
                />
              </div>

              <div className="ays-field">
                <label className="ays-label">
                  Room Number <span className="ays-label-optional">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.roomNumber}
                  onChange={(e) => handleInputChange("roomNumber", e.target.value)}
                  className="ays-input"
                  placeholder="e.g., 101, A-205"
                />
              </div>

              <div className="ays-field ays-field--full">
                <label className="ays-label">
                  Company / Office Name <span className="ays-label-optional">(Required)</span>
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange("companyName", e.target.value)}
                  className="ays-input"
                  placeholder="e.g., ABC Corporation, Tech Park Office"
                />
              </div>

              <div className="ays-field ays-field--full">
                <label className="ays-label">
                  Room Description <span className="ays-label-optional">(Optional)</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  rows="4"
                  className="ays-textarea"
                  placeholder="Describe capacity, layout, or special features..."
                />
              </div>

              <div className="ays-field ays-field--full">
                <label className="ays-label">
                  Room Visibility <span className="ays-label-required">*</span>
                </label>
                <div className="ays-visibility-options">
                  <label className="ays-visibility-option">
                    <input
                      type="radio"
                      name="visibility"
                      value="private"
                      checked={formData.visibility === "private"}
                      onChange={(e) => handleInputChange("visibility", e.target.value)}
                    />
                    <div className="ays-visibility-card">
                      <strong>Private Room</strong>
                      <span>Only your company members can view and book this room.</span>
                    </div>
                  </label>
                  <label className="ays-visibility-option">
                    <input
                      type="radio"
                      name="visibility"
                      value="public"
                      checked={formData.visibility === "public"}
                      onChange={(e) => handleInputChange("visibility", e.target.value)}
                    />
                    <div className="ays-visibility-card">
                      <strong>Public Room</strong>
                      <span>Visible to all users. Anyone registered can book.</span>
                    </div>
                  </label>
                </div>
              </div>

              <div className="ays-field ays-field--full">
                <label className="ays-label">Amenities</label>
                <div className="ays-amenities">
                  {availableAmenities.map((amenity) => {
                    const active = formData.amenities.includes(amenity);
                    return (
                      <label
                        key={amenity}
                        className={`ays-amenity-chip ${active ? "ays-amenity-chip--active" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => handleAmenityToggle(amenity)}
                        />
                        <span className="ays-amenity-icon">{amenityIcons[amenity] || "✓"}</span>
                        {amenity}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="ays-actions">
              {editingRoomIndex !== null && (
                <button type="button" onClick={resetForm} className="ays-btn ays-btn--secondary">
                  Cancel
                </button>
              )}
              <button type="submit" disabled={loading} className="ays-btn ays-btn--primary">
                {loading ? "Saving..." : editingRoomIndex !== null ? "Update Room" : "Add Room"}
              </button>
            </div>
          </form>
        </section>

        <section className="ays-card">
          <div className="ays-card-header">
            <div>
              <h2 className="ays-card-title">
                Your Rooms
                <span className="ays-rooms-count">{rooms.length}</span>
              </h2>
              <p className="ays-card-desc">Rooms you have added to your workspace.</p>
            </div>
          </div>

          {isLoadingRooms ? (
            <div className="ays-loading">
              <div className="ays-loading-spinner" />
              <p>Loading your rooms...</p>
            </div>
          ) : rooms.length === 0 ? (
            <div className="ays-empty">
              <div className="ays-empty-icon">
                <FaBuilding />
              </div>
              <p className="ays-empty-title">No rooms added yet</p>
              <p className="ays-empty-desc">Use the form above to add your first meeting room.</p>
            </div>
          ) : (
            <div className="ays-room-grid">
              {rooms.map((room, index) => (
                <article key={index} className="ays-room-card">
                  <div className="ays-room-card-top">
                    <div>
                      <h3 className="ays-room-name">{room.roomName}</h3>
                      {room.roomNumber && (
                        <p className="ays-room-meta">Room #{room.roomNumber}</p>
                      )}
                      {room.companyName && (
                        <p className="ays-room-company">{room.companyName}</p>
                      )}
                      <span
                        className={`ays-room-badge ${
                          room.visibility === "public" ? "ays-room-badge--public" : "ays-room-badge--private"
                        }`}
                      >
                        {room.visibility === "public" ? "Public" : "Private"}
                      </span>
                    </div>
                    <div className="ays-room-actions">
                      <button
                        type="button"
                        onClick={() => handleEdit(index)}
                        className="ays-icon-btn ays-icon-btn--edit"
                        title="Edit Room"
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(index)}
                        className="ays-icon-btn ays-icon-btn--delete"
                        title="Delete Room"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  {room.description && <p className="ays-room-desc">{room.description}</p>}

                  {room.amenities?.length > 0 && (
                    <div className="ays-room-amenities">
                      {room.amenities.map((amenity, amenityIndex) => (
                        <span key={amenityIndex} className="ays-room-amenity-tag">
                          {amenity}
                        </span>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AddYourSpace;

